import { useMemo, useState } from 'react'
import type { SafetyFunction } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  defaultSafetyFunctionFormData,
  validateSafetyFunctionCreateForm,
} from './safetyFunctionForm.schema'

type CreateSafetyFunctionDialogProps = {
  isOpen: boolean
  onClose: () => void
  onCreate: (payload: { name: string; description: string }) => void
  safetyFunction?: SafetyFunction | null
  onUpdate?: (payload: {
    safetyFunctionId: string
    name: string
    description: string
  }) => void
  isSaving?: boolean
}

export function CreateSafetyFunctionDialog({
  isOpen,
  onClose,
  onCreate,
  safetyFunction,
  onUpdate,
  isSaving = false,
}: CreateSafetyFunctionDialogProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const isEdit = safetyFunction != null
  const [name, setName] = useState(safetyFunction?.name ?? '')
  const [description, setDescription] = useState(
    safetyFunction?.description ?? ''
  )

  const isValid = useMemo(() => {
    return validateSafetyFunctionCreateForm({ name, description }).success
  }, [name, description])

  const handleSubmit = () => {
    if (!isValid) {
      return
    }
    if (isEdit && onUpdate && safetyFunction) {
      onUpdate({
        safetyFunctionId: safetyFunction.id,
        name: name.trim(),
        description: description.trim(),
      })
      return
    }
    onCreate({
      name: name.trim(),
      description: description.trim(),
    })
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleClose()
    }
  }

  const handleClose = () => {
    setName(safetyFunction?.name ?? defaultSafetyFunctionFormData.name)
    setDescription(
      safetyFunction?.description ?? defaultSafetyFunctionFormData.description
    )
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent showCloseButton={!isSaving}>
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t('safetyFunctionsPage.renameDialog.title')
              : t('safetyFunctionsPage.createDialog.title')}
          </DialogTitle>
        </DialogHeader>
        <div className='space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor='sf-name'>
              {t('safetyFunctionsPage.createDialog.name')}{' '}
              <span className='text-destructive'>*</span>
            </Label>
            <Input
              id='sf-name'
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t(
                'safetyFunctionsPage.createDialog.namePlaceholder'
              )}
            />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='sf-description'>
              {t('safetyFunctionsPage.createDialog.description')}
            </Label>
            <Textarea
              id='sf-description'
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={t(
                'safetyFunctionsPage.createDialog.descriptionPlaceholder'
              )}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={handleClose} disabled={isSaving}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || isSaving}>
            {isEdit
              ? t('safetyFunctionsPage.renameDialog.save')
              : t('safetyFunctionsPage.createDialog.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
